import { useMemo, useRef, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { PersonPicker } from "@/components/board/PersonPicker";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { useWorkspaceMemberMutations } from "@/hooks/useWorkspaceMemberMutations";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { resolveWorkspaceColor, deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID, UserMe, Workspace, WorkspaceMember, WorkspaceRole } from "@/lib/types";

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Mitglied",
  viewer: "Viewer",
};

export function WorkspaceMembersSection() {
  const wsQuery = useQuery<Workspace[]>({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
  });
  const meQuery = useQuery<UserMe>({
    queryKey: queryKeys.me,
    queryFn: api.me,
  });

  if (!wsQuery.data || !meQuery.data) {
    return <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{t.loading}</div>;
  }
  if (wsQuery.data.length === 0) {
    return <div style={emptyHintStyle}>{t.membersSectionEmpty}</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {wsQuery.data.map((ws) => (
        <WorkspacePanel key={ws.id} workspace={ws} me={meQuery.data} />
      ))}
    </div>
  );
}

function WorkspacePanel({ workspace, me }: { workspace: Workspace; me: UserMe }) {
  const members = useWorkspaceMembers(workspace.id as UUID);
  const mut = useWorkspaceMemberMutations(workspace.id as UUID);
  const [pickerOpen, setPickerOpen] = useState(false);
  const addBtnRef = useRef<HTMLDivElement | null>(null);

  const meRole = useMemo<WorkspaceRole | null>(() => {
    const m = (members.data ?? []).find((m) => m.user_id === me.id);
    return (m?.role as WorkspaceRole | undefined) ?? null;
  }, [members.data, me.id]);
  const ownerCount = useMemo(
    () => (members.data ?? []).filter((m) => m.role === "owner").length,
    [members.data],
  );

  if (!meRole || (meRole !== "owner" && meRole !== "admin")) return null;

  const dot = resolveWorkspaceColor(workspace);

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: dot,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>
          {workspace.name}
        </span>
        <span style={{ flex: 1 }} />
        <div ref={addBtnRef}>
          <Button variant="secondary" size="sm" icon="plus" onClick={() => setPickerOpen(true)}>
            {t.memberAdd}
          </Button>
        </div>
      </div>

      <div style={listStyle}>
        {(members.data ?? []).map((m) => (
          <MemberRow
            key={m.user_id}
            workspaceId={workspace.id as UUID}
            member={m}
            actorRole={meRole}
            actorId={me.id as UUID}
            ownerCount={ownerCount}
            onChangeRole={(role) =>
              mut.updateRole.mutate(
                { userId: m.user_id as UUID, role },
                {
                  onError: (e) => {
                    const msg =
                      (e as { status?: number }).status === 409
                        ? t.memberLastOwnerError
                        : t.unknownError;
                    showToast(msg, "error");
                  },
                },
              )
            }
            onRemove={() =>
              mut.remove.mutate(m.user_id as UUID, {
                onError: (e) => {
                  const msg =
                    (e as { status?: number }).status === 409
                      ? t.memberLastOwnerError
                      : t.unknownError;
                  showToast(msg, "error");
                },
              })
            }
          />
        ))}
      </div>

      {pickerOpen ? (
        <PersonPicker
          anchor={addBtnRef.current}
          value={[]}
          allowMultiple={false}
          excludeUserIds={(members.data ?? []).map((m) => m.user_id as UUID)}
          onClose={() => setPickerOpen(false)}
          onChange={([userId]) => {
            if (userId) {
              mut.add.mutate(
                { user_id: userId, role: "member" },
                {
                  onError: (e) => {
                    const msg =
                      (e as { status?: number }).status === 409 ? t.unknownError : t.unknownError;
                    showToast(msg, "error");
                  },
                },
              );
            }
            setPickerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function MemberRow({
  workspaceId,
  member,
  actorRole,
  actorId,
  ownerCount,
  onChangeRole,
  onRemove,
}: {
  workspaceId: UUID;
  member: WorkspaceMember;
  actorRole: WorkspaceRole;
  actorId: UUID;
  ownerCount: number;
  onChangeRole: (role: WorkspaceRole) => void;
  onRemove: () => void;
}) {
  void workspaceId;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isSelf = member.user_id === actorId;
  const isLastOwner = member.role === "owner" && ownerCount <= 1;
  const name = member.display_name ?? member.email;
  const avatarUser = {
    id: member.user_id,
    display_name: name,
    initials: deriveInitials(name),
    avatar_color: deriveAvatarColor(member.user_id),
    username: member.username,
    department_workspace_id: member.department_workspace_id ?? null,
    department_color: member.department_color ?? null,
  };

  // Role edit rights: owners can edit anyone (subject to last-owner rule);
  // admins can edit only member/viewer (not other admins/owners).
  const canEditRole =
    actorRole === "owner" ||
    (actorRole === "admin" && member.role !== "admin" && member.role !== "owner");
  // Remove rights mirror role-edit, plus everyone can leave themselves
  // (subject to last-owner rule).
  const canRemove = (canEditRole || isSelf) && !isLastOwner;

  return (
    <div style={rowStyle}>
      <Avatar user={avatarUser} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--ink-1)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
          {isSelf ? (
            <span
              style={{
                marginLeft: 6,
                color: "var(--ink-4)",
                fontWeight: 400,
                fontSize: 11.5,
              }}
            >
              ({t.memberYou})
            </span>
          ) : null}
          {member.username ? (
            <span
              style={{
                marginLeft: 6,
                color: "var(--ink-4)",
                fontWeight: 400,
                fontSize: 11.5,
              }}
            >
              · {member.username.toUpperCase()}
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--ink-3)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {member.email}
        </div>
      </div>
      {canEditRole ? (
        <select
          value={member.role}
          onChange={(e) => onChangeRole(e.target.value as WorkspaceRole)}
          disabled={isLastOwner}
          style={roleSelectStyle}
          aria-label={t.settingsRoles}
        >
          {(actorRole === "owner"
            ? (["owner", "admin", "member", "viewer"] as const)
            : (["admin", "member", "viewer"] as const)
          ).map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      ) : (
        <span
          style={{
            fontSize: 12,
            color: "var(--ink-3)",
            padding: "4px 10px",
            borderRadius: 6,
            background: "var(--surface-soft)",
          }}
        >
          {ROLE_LABELS[member.role]}
        </span>
      )}
      {canRemove ? (
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          title={t.memberRemove}
          aria-label={t.memberRemove}
          style={removeBtnStyle}
        >
          <Icon name="x" size={13} color="var(--ink-3)" />
        </button>
      ) : (
        <span style={{ width: 24 }} />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={isSelf ? t.memberLeaveTitle : t.memberRemoveTitle}
        message={isSelf ? t.memberLeaveMessage : t.memberRemoveMessage.replace("{name}", name)}
        confirmLabel={t.memberRemove}
        tone="danger"
        onConfirm={() => {
          onRemove();
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}

const panelStyle: CSSProperties = {
  background: "var(--surface)",
  borderRadius: 10,
  border: "1px solid var(--hairline)",
};
const panelHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  borderBottom: "1px solid var(--hairline-soft)",
};
const listStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 14px",
  borderBottom: "1px solid var(--hairline-soft)",
};
const roleSelectStyle: CSSProperties = {
  height: 30,
  padding: "0 8px",
  borderRadius: 6,
  border: "1px solid var(--hairline-strong)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  fontSize: 12.5,
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  cursor: "pointer",
};
const removeBtnStyle: CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 6,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const emptyHintStyle: CSSProperties = {
  padding: "14px 16px",
  borderRadius: 8,
  background: "var(--surface-soft)",
  border: "1px dashed var(--hairline-strong)",
  color: "var(--ink-3)",
  fontSize: 13,
};
